
import { defineComponent, reactive, watch } from 'vue';
import Icone from '@/core/components/Icone.vue';
import Card from '@/core/components/Tela/Card.vue';
import CampoNumerico from '@/core/components/Tela/CampoNumerico.vue';
import SelecionarSimNao from '@/core/components/Tela/SelecionarSimNao.vue';
import SelecionarData from '@/core/components/Tela/SelecionarData.vue';
import { useTelaBase } from '@/core/composables/TelaBase';
import { useModalBase } from '@/core/composables/ModalBase';
import { ITelaOperacao } from '@/core/models/ITelaOperacao';
import RequisicaoModal from '@/core/components/Modal/RequisicaoModal.vue';
import { EStatusRetornoRequisicao, IRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import { ETipoPermissao } from '@/models/Enumeradores/MeuSistema/Usuarios/ETipoPermissao';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import { IConta, IContaSaldoInicial, IContaUsuario } from '@/models/Entidades/Cadastros/Financeiro/IConta';
import ServicoConta from '@/servicos/Cadastros/Financeiro/ServicoConta';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import SelecionarEmpresa from '@/components/MeuSistema/Empresas/SelecionarEmpresa.vue';
import SelecionarTipoConta from '@/components/Cadastros/Financeiro/SelecionarTipoConta.vue';
import SelecionarBanco from '@/components/Cadastros/Financeiro/SelecionarBanco.vue';
import SelecionarPessoa from '@/components/Cadastros/Pessoas/SelecionarPessoa.vue';
import SelecionarTipoDocumentoFinanceiro from '@/components/Cadastros/Financeiro/SelecionarTipoDocumentoFinanceiro.vue';
import { ETipoConta } from '@/models/Enumeradores/Cadastros/Financeiro/ETipoConta';
import SelecionarUsuario from '@/components/MeuSistema/Usuarios/SelecionarUsuario.vue';
import SelecionarCategoriaPlanoConta from '@/components/Cadastros/PlanosContas/SelecionarCategoriaPlanoConta.vue';
import { IBanco } from '@/models/Entidades/Cadastros/Financeiro/IBanco';
import storeSistema from '@/store/storeSistema';
import ServicoSistema from '@/servicos/MeuSistema/ServicoSistema';
import { ETipoContaBancaria } from '@/models/Enumeradores/Cadastros/Financeiro/ETipoContaBancaria';
import router from '@/router';
import { IContaBancaria } from '@/models/Entidades/Cadastros/Financeiro/IContaBancaria';
import ServicoMovimentoFinanceiro from '@/servicos/Financeiro/ServicoMovimentoFinanceiro';
import ConfiguracaoBoletosModal from './ConfiguracaoBoletosModal.vue';
import { IConfiguracaoBoletos } from '@/models/Entidades/Cadastros/Financeiro/IConfiguracoesBoletos';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';

export default defineComponent({
  name: 'ContaModal',
  props: {
    visivel: {
      type: Boolean,
    },
    operacao: {
      type: Object as () => ITelaOperacao,
      required: true,
    },
  },
  components: {
    RequisicaoModal,
    Icone,
    Card,
    SelecionarTipoConta,
    SelecionarData,
    CampoNumerico,
    SelecionarSimNao,
    SelecionarEmpresa,
    SelecionarBanco,
    SelecionarPessoa,
    SelecionarUsuario,
    SelecionarCategoriaPlanoConta,
    SelecionarTipoDocumentoFinanceiro,
    ConfiguracaoBoletosModal,
  },
  emits: ['sincronizarRegistro', 'update:operacao', 'update:visivel'],
  setup(props, { emit }) {
    const {
      telaBase, obterPermissoes, preencherEmpresasComEstrategiaPermissaoDados, verificaConceitoParaApresentarEmpresas,
      preencherPermissoesDados, filtrarPermissaoDadosUsuario, apresentarMensagemSucesso, apresentarMensagemAlerta,
    } = useTelaBase();
    const {
      modalBase, apresentarRetornoRequisicao, apresentarBarraProgresso, ocultarBarraProgresso, sincronizarRegistro,
    } = useModalBase(props, emit);
    const servicoConta = new ServicoConta();
    const servicoMovimentoFinanceiro = new ServicoMovimentoFinanceiro();
    servicoMovimentoFinanceiro.requisicaoSistema();

    const servicoSistema = new ServicoSistema();

    telaBase.identificadorRecurso = 'CADASTRO_CONTAS';
    telaBase.identificadorPermissao = 'PER_CADASTRO_CONTAS';

    const state = reactive({
      conta: {} as IConta,
      banco: {} as IBanco,
      saldoAtual: 0,
      usuarios: [] as number[],
      carregandoBanco: false,
      exibirModalContaBancaria: false,
      exibirModalTipoDocumentoFinanceiro: false,
      exibirModalConfiguracaoBoletos: false,
      operacaoContaBancaria: {} as ITelaOperacao,
      nomeBanco: '',
    });

    function iniciarBanco() {
      state.conta.codigoBanco = 0;
      state.banco = {} as IBanco;
      state.banco.logo = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3PTWBSGcbGzM6GCKqlIBRV0dHRJFarQ0eUT8LH4BnRU0NHR0UEFVdIlFRV7TzRksomPY8uykTk/zewQfKw/9znv4yvJynLv4uLiV2dBoDiBf4qP3/ARuCRABEFAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghgg0Aj8i0JO4OzsrPv69Wv+hi2qPHr0qNvf39+iI97soRIh4f3z58/u7du3SXX7Xt7Z2enevHmzfQe+oSN2apSAPj09TSrb+XKI/f379+08+A0cNRE2ANkupk+ACNPvkSPcAAEibACyXUyfABGm3yNHuAECRNgAZLuYPgEirKlHu7u7XdyytGwHAd8jjNyng4OD7vnz51dbPT8/7z58+NB9+/bt6jU/TI+AGWHEnrx48eJ/EsSmHzx40L18+fLyzxF3ZVMjEyDCiEDjMYZZS5wiPXnyZFbJaxMhQIQRGzHvWR7XCyOCXsOmiDAi1HmPMMQjDpbpEiDCiL358eNHurW/5SnWdIBbXiDCiA38/Pnzrce2YyZ4//59F3ePLNMl4PbpiL2J0L979+7yDtHDhw8vtzzvdGnEXdvUigSIsCLAWavHp/+qM0BcXMd/q25n1vF57TYBp0a3mUzilePj4+7k5KSLb6gt6ydAhPUzXnoPR0dHl79WGTNCfBnn1uvSCJdegQhLI1vvCk+fPu2ePXt2tZOYEV6/fn31dz+shwAR1sP1cqvLntbEN9MxA9xcYjsxS1jWR4AIa2Ibzx0tc44fYX/16lV6NDFLXH+YL32jwiACRBiEbf5KcXoTIsQSpzXx4N28Ja4BQoK7rgXiydbHjx/P25TaQAJEGAguWy0+2Q8PD6/Ki4R8EVl+bzBOnZY95fq9rj9zAkTI2SxdidBHqG9+skdw43borCXO/ZcJdraPWdv22uIEiLA4q7nvvCug8WTqzQveOH26fodo7g6uFe/a17W3+nFBAkRYENRdb1vkkz1CH9cPsVy/jrhr27PqMYvENYNlHAIesRiBYwRy0V+8iXP8+/fvX11Mr7L7ECueb/r48eMqm7FuI2BGWDEG8cm+7G3NEOfmdcTQw4h9/55lhm7DekRYKQPZF2ArbXTAyu4kDYB2YxUzwg0gi/41ztHnfQG26HbGel/crVrm7tNY+/1btkOEAZ2M05r4FB7r9GbAIdxaZYrHdOsgJ/wCEQY0J74TmOKnbxxT9n3FgGGWWsVdowHtjt9Nnvf7yQM2aZU/TIAIAxrw6dOnAWtZZcoEnBpNuTuObWMEiLAx1HY0ZQJEmHJ3HNvGCBBhY6jtaMoEiJB0Z29vL6ls58vxPcO8/zfrdo5qvKO+d3Fx8Wu8zf1dW4p/cPzLly/dtv9Ts/EbcvGAHhHyfBIhZ6NSiIBTo0LNNtScABFyNiqFCBChULMNNSdAhJyNSiECRCjUbEPNCRAhZ6NSiAARCjXbUHMCRMjZqBQiQIRCzTbUnAARcjYqhQgQoVCzDTUnQIScjUohAkQo1GxDzQkQIWejUogAEQo121BzAkTI2agUIkCEQs021JwAEXI2KoUIEKFQsw01J0CEnI1KIQJEKNRsQ80JECFno1KIABEKNdtQcwJEyNmoFCJAhELNNtScABFyNiqFCBChULMNNSdAhJyNSiECRCjUbEPNCRAhZ6NSiAARCjXbUHMCRMjZqBQiQIRCzTbUnAARcjYqhQgQoVCzDTUnQIScjUohAkQo1GxDzQkQIWejUogAEQo121BzAkTI2agUIkCEQs021JwAEXI2KoUIEKFQsw01J0CEnI1KIQJEKNRsQ80JECFno1KIABEKNdtQcwJEyNmoFCJAhELNNtScABFyNiqFCBChULMNNSdAhJyNSiECRCjUbEPNCRAhZ6NSiAARCjXbUHMCRMjZqBQiQIRCzTbUnAARcjYqhQgQoVCzDTUnQIScjUohAkQo1GxDzQkQIWejUogAEQo121BzAkTI2agUIkCEQs021JwAEXI2KoUIEKFQsw01J0CEnI1KIQJEKNRsQ80JECFno1KIABEKNdtQcwJEyNmoFCJAhELNNtScABFyNiqFCBChULMNNSdAhJyNSiEC/wGgKKC4YMA4TAAAAABJRU5ErkJggg==';
    }

    async function iniciarSaldo() {
      state.conta.saldoInicial = {} as IContaSaldoInicial;
      state.conta.saldoInicial.codigoTipoDocumentoFinanceiro = 0;
      state.conta.saldoInicial.codigoPlanoContaCategoria = 0;
      state.conta.saldoInicial.data = await servicoSistema.obterDataAtual();
      state.conta.saldoInicial.saldo = 0;
    }

    function iniciarTela() {
      state.conta = {} as IConta;
      state.conta.codigo = 0;
      state.conta.tipo = ETipoConta.Cofre;
      state.conta.codigoContaBancaria = 0;
      state.conta.ativo = true;
      state.conta.contasReceber = true;
      state.conta.contasPagar = true;
      state.conta.codigoEmpresa = storeSistema.getters.codigoEmpresaOperacao();
      state.conta.contaBancaria = {} as IContaBancaria;
      state.conta.contaBancaria.contaEmpresa = true;
      state.exibirModalConfiguracaoBoletos = false;
      state.nomeBanco = '';
      iniciarBanco();
      iniciarSaldo();
    }

    function validarTipoConta() {
      if (state.conta.tipo === ETipoConta.ContaCorrente || state.conta.tipo === ETipoConta.ContaPoupanca) {
        if (!UtilitarioGeral.objetoValido(state.conta.contaBancaria)) {
          state.conta.contaBancaria = {} as IContaBancaria;
          state.conta.contaBancaria.codigo = 0;
          state.conta.contaBancaria.ativo = true;
          state.conta.contaBancaria.tipo = state.conta.tipo === ETipoConta.ContaCorrente ? ETipoContaBancaria.ContaCorrente : ETipoContaBancaria.ContaPoupanca;
        }
      } else {
        iniciarBanco();
      }
    }

    function limparUsuarios() {
      if (!state.conta.usuariosEspecificos) {
        state.usuarios = [] as number[];
      }
    }

    function obterUsuarios() {
      state.conta.usuarios = [] as IContaUsuario[];
      state.usuarios.forEach((u) => {
        const usuario = {} as IContaUsuario;
        usuario.codigoConta = state.conta.codigo;
        usuario.codigoUsuario = u;
        state.conta.usuarios.push(usuario);
      });
    }

    function validarCamposConta() {
      if (state.conta.codigoBanco === 0 || state.conta.codigoBanco === undefined) {
        apresentarMensagemAlerta('O banco deve ser informado!');
        return false;
      }

      if (state.conta.contaBancaria.agencia === '' || state.conta.contaBancaria.agencia === undefined) {
        apresentarMensagemAlerta('A agência deve ser informada!');
        return false;
      }

      if (state.conta.contaBancaria.conta === '' || state.conta.contaBancaria.conta === undefined) {
        apresentarMensagemAlerta('O número da conta deve ser informado!');
        return false;
      }

      if (state.conta.contaBancaria.contaDv === '' || state.conta.contaBancaria.contaDv === undefined) {
        apresentarMensagemAlerta('O dígito da conta deve ser informado!');
        return false;
      }

      return true;
    }

    async function salvar(salvarNovo: boolean) {
      if (state.conta.tipo === ETipoConta.ContaCorrente || state.conta.tipo === ETipoConta.ContaPoupanca) {
        state.conta.contaBancaria.codigoEmpresa = state.conta.codigoEmpresa;
        state.conta.contaBancaria.codigoBanco = state.conta.codigoBanco;
        state.conta.contaBancaria.tipo = state.conta.tipo === ETipoConta.ContaCorrente ? ETipoContaBancaria.ContaCorrente : ETipoContaBancaria.ContaPoupanca;
        const validacao = validarCamposConta();
        if (!validacao) return;
      }

      let retorno: IRetornoRequisicao = { codigoRegistro: 0, status: 0, mensagem: '' };
      obterUsuarios();
      apresentarBarraProgresso();
      if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
        retorno = await servicoConta.incluir(state.conta);
      } else if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
        retorno = await servicoConta.alterar(state.conta);
      }
      ocultarBarraProgresso();
      if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
          sincronizarRegistro(EPermissaoDados.Incluir, retorno.codigoRegistro);
        } else {
          sincronizarRegistro(EPermissaoDados.Alterar, props.operacao.codigoRegistro);
        }
        apresentarMensagemSucesso(retorno.mensagem);
        if (salvarNovo) {
          iniciarTela();
          const telaOperacao: ITelaOperacao = props.operacao;
          telaOperacao.codigoRegistro = 0;
          telaOperacao.tipoPermissaoDados = EPermissaoDados.Incluir;
          modalBase.computedOperacao = telaOperacao;
        } else {
          modalBase.computedVisivel = false;
        }
      } else {
        apresentarRetornoRequisicao(retorno);
      }
    }

    function redirecionarMovimentos() {
      if (state.conta.codigo === undefined || state.conta.codigo === 0) {
        apresentarMensagemAlerta('Não é possível redirecionar para as movimentações antes da conta ser inserida.');
        return;
      }
      router.push({ name: 'MovimentacoesContas', params: { empresa: state.conta.codigoEmpresa, conta: state.conta.codigo } });
    }

    function preencherDescricaoDadosBancarios() {
      let descricao = '';
      const banco = state.nomeBanco.split('-');
      descricao += `${banco[1]} - `;
      if (UtilitarioGeral.valorValido(state.conta.contaBancaria.agencia)) {
        descricao += `Ag: ${state.conta.contaBancaria.agencia}`;
        if (UtilitarioGeral.valorValido(state.conta.contaBancaria.agenciaDv)) {
          descricao += `-${state.conta.contaBancaria.agenciaDv}`;
        }
      }

      if (UtilitarioGeral.valorValido(state.conta.contaBancaria.conta)) {
        descricao += ' Conta: ';
        descricao += state.conta.contaBancaria.conta;
        if (UtilitarioGeral.valorValido(state.conta.contaBancaria.contaDv)) {
          descricao += `-${state.conta.contaBancaria.contaDv}`;
        }
      }

      state.conta.descricao = descricao;
    }

    function abrirConfiguracaoBoletos() {
      if (state.conta.codigoBanco === 0 || state.conta.codigoBanco === null) {
        apresentarMensagemAlerta('Você deve definir primeiro o banco para poder configurar os boletos!');
        return;
      }
      state.exibirModalConfiguracaoBoletos = true;
    }

    function atualizarConfiguracaoBoletos(configuracao: IConfiguracaoBoletos) {
      state.conta.configuracaoBoletos = configuracao;
    }

    watch(async () => modalBase.computedVisivel, async () => {
      telaBase.carregando = true;
      iniciarTela();
      if (modalBase.computedVisivel) {
        if (props.operacao.listaPermissoesDados.length > 0) {
          await preencherPermissoesDados(props.operacao.listaPermissoesDados);
        } else {
          await obterPermissoes(ETipoPermissao.Dados);
        }
        await preencherEmpresasComEstrategiaPermissaoDados(props.operacao.tipoPermissaoDados, false);
        verificaConceitoParaApresentarEmpresas();
        telaBase.permissaoDados = await filtrarPermissaoDadosUsuario(state.conta.codigoEmpresa);

        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
          modalBase.textoBotaoSalvar = 'Salvar alterações';
          modalBase.textoBotaoSalvarNovo = 'Salvar e novo';
          state.conta = await servicoConta.obter(props.operacao.codigoRegistro);
          state.saldoAtual = await servicoMovimentoFinanceiro.obterSaldoAtual(state.conta.codigo, state.conta.codigoEmpresa);
          telaBase.empresasSelecionadas = [];
        } else {
          modalBase.textoBotaoSalvar = 'Concluir cadastro';
          modalBase.textoBotaoSalvarNovo = 'Concluir e novo';
        }
      }
      telaBase.carregando = false;
    });

    return {
      telaBase,
      props,
      modalBase,
      EPermissaoDados,
      ETipoConta,
      UtilitarioGeral,
      apresentarRetornoRequisicao,
      state,
      iniciarTela,
      salvar,
      limparUsuarios,
      validarTipoConta,
      redirecionarMovimentos,
      preencherDescricaoDadosBancarios,
      abrirConfiguracaoBoletos,
      atualizarConfiguracaoBoletos,
      UtilitarioData,
    };
  },
});
